import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import mountainImg from "../../assets/images/information/image5.png";
import mountainImg3 from "../../assets/images/information/image7.png";
import mountainImg2 from "../../assets/images/information/image6.png";

import backgroundImg from "../../assets/images/information/image6.jpg";
import iceTexture from "../../assets/images/information/image5.jpg";

import everestPDF from "../../assets/docs/itinerary/EBC_Trek_Deluxe.pdf";
import k2PDF from "../../assets/docs/itinerary/BBA_K2_TREK_2026.pdf";

/* ROUTE MAPS */
import EverestRouteMap from "../routes/EverestRouteMap";
// import K2RouteMap from "../routes/K2RouteMap";

const peaks = [
  {
    name: "Everest Basecamp Trek",
    height: "5,545 m",
    image: mountainImg,
    expedition:
      "Everest Base Camp (EBC) Trek is a once-in-a-lifetime adventure through the Khumbu Valley.",
    details: [
      // { label: "Price per person", value: "USD $2,500.00" },
      { label: "Country", value: "Nepal" },
      { label: "Route", value: "Standard" },
      { label: "Range", value: "Khumbu Himal" },
      { label: "Best Season", value: "Spring/Autumn" },
    ],
    pdf: everestPDF,
    routeMap: EverestRouteMap,
  },
  {
    name: "K2 Basecamp & Gondogoro La Trek",
    height: "5,585 m",
    image: mountainImg2,
    expedition:
      "Challenging high-altitude trek through the Karakoram.",
    details: [
      // { label: "Price per person", value: "USD $2,500.00" },
      { label: "Country", value: "Pakistan" },
      { label: "Route", value: "Via Skardu" },
      { label: "Range", value: "Karakoram" },
      { label: "Best Season", value: "Summer" },
    ],
    pdf: k2PDF,
    // routeMap: K2RouteMap
  },
  {
    name: "Annapurna Basecamp Trek",
    height: "4,130 m",
    image: mountainImg3,
    expedition:
      "A classic Himalayan trek through the Annapurna Sanctuary.",
    details: [
      // { label: "Price per person", value: "USD $2,000.00" },
      { label: "Country", value: "Nepal" },
      { label: "Route", value: "Annapurna Sanctuary (Standard)" },
      { label: "Range", value: "Himalaya" },
      { label: "Best Season", value: "Spring/Autumn" },
    ],
  },
  {
    name: "Nanga Parbat Basecamp Trek",
    height: "4,200 m",
    image: mountainImg3,
    expedition:
      "A scenic trek via Fairy Meadows to the base of Nanga Parbat.",
    details: [
      // { label: "Price per person", value: "USD $1000.00" },
      { label: "Country", value: "Pakistan" },
      { label: "Route", value: "Fairy Meadows, Raikot Glacier" },
      { label: "Range", value: "Western Himalaya" },
      { label: "Best Season", value: "Summer" },
    ],
  },
];

const TrekkingInfo = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();

  const totalPathLength = 3150;
  const visibleStart = 190;
  const animatedLength = totalPathLength - visibleStart;

  const pathLength = useTransform(
    scrollYProgress,
    [0, 1],
    [0, animatedLength / totalPathLength]
  );

  return (
    <div ref={containerRef} className="relative w-full bg-black text-white">
      {/* ICE TEXTURE BACKGROUND — UNTOUCHED */}
      <div className="absolute top-0 left-0 w-full h-full z-0 overflow-hidden">
        <motion.div
          className="fixed top-0 left-0 w-full h-full pointer-events-none -z-10"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -80]) }}
        >
          <img
            src={backgroundImg}
            className="absolute top-0 left-0 w-full h-full object-cover opacity-30"
            alt="far mountains"
          />
        </motion.div>
      </div>

      {/* CURVED ICE LINE — UNTOUCHED */}
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none z-10"
        viewBox="0 0 100 3000"
        preserveAspectRatio="none"
      >
        <defs>
          <pattern
            id="icePattern"
            x="0"
            y="0"
            width="100"
            height="100"
            patternUnits="userSpaceOnUse"
          >
            <image href={iceTexture} width="100" height="100" />
          </pattern>
          <filter id="glow">
            <feGaussianBlur stdDeviation="0.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.path
          d="
            M 50 0
            C 33 1050, 67 1200, 50 1550
            C 33 1850, 67 2150, 50 2450
            C 33 2750, 67 2950, 50 3150
            
          "
          fill="none"
          stroke="url(#icePattern)"
          strokeWidth="1.5"
          filter="url(#glow)"
          strokeDasharray={totalPathLength}
          style={{
            strokeDashoffset: useTransform(
              pathLength,
              (v) => totalPathLength - visibleStart - v * totalPathLength
            ),
          }}
        />
      </svg>

      {/* CONTENT */}
      <div className="flex flex-col relative z-20">
        {peaks.map((peak, index) => (
          <Section key={index} peak={peak} index={index} />
        ))}
      </div>
    </div>
  );
};

const Section = ({ peak, index }) => {
  const ref = useRef(null);
  const isEven = index % 2 === 0;
  const [openRoute, setOpenRoute] = useState(false);

  const RouteComponent = peak.routeMap;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "start 0.2"],
  });

  const textOpacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const imgOpacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <>
      <div
        ref={ref}
        className="grid grid-cols-1 md:grid-cols-2 items-center w-[90%] mx-auto min-h-screen my-auto mt-32 md:mt-0 lg:mt-10"
      >
        <motion.div
          style={{ opacity: textOpacity }}
          className={`${isEven ? "pr-10" : "pl-10"}`}
        >
          <h2 className="text-5xl font-bold">{peak.name}</h2>
          <p className="text-yellow-400 text-xl mb-4">{peak.height}</p>

          <p className="text-gray-300 mb-6">{peak.expedition}</p>

          <motion.ul className="text-gray-300 mb-10 space-y-2">
            {peak.details.map((item, i) => (
              <li key={i}>
                <span className="font-semibold text-[#9bb7d4]">
                  {item.label}:
                </span>{" "}
                {item.value}
              </li>
            ))}
          </motion.ul>

          <div className="flex gap-4 mt-6">
            {peak.pdf && (
              <a
                href={peak.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition"
              >
                View Itinerary
              </a>
            )}

            {/* ✅ SHOW ONLY IF THIS PEAK HAS A ROUTE */}
            {RouteComponent && (
              <button
                onClick={() => setOpenRoute(true)}
                className="border border-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition"
              >
                View Route
              </button>
            )}

            
              <a
                href="/contact"
                className="inline-block border border-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition"
              >
                Book Now
              </a>
          </div>
        </motion.div>

        <motion.div
          style={{
            opacity: imgOpacity,
            scale: useTransform(scrollYProgress, [0, 1], [0.6, 1]),
          }}
          className={`${isEven ? "order-2 md:order-2 z-20" : "order-2 md:order-1 z-20"}`}
        >
          <img
            src={peak.image}
            className="w-full h-[500px] object-contain"
            alt={peak.name}
          />
        </motion.div>
      </div>

      {/* ROUTE MODAL — ONLY FOR THAT PEAK */}
      {openRoute && RouteComponent && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-[9999]"
          onClick={() => setOpenRoute(false)}
        >
          <div
            className="bg-black/15 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenRoute(false)}
              className="absolute top-2 right-2 text-white text-xl hover:text-red-400"
            >
              ✕
            </button>

            <div className="w-[90vw] max-w-[680px] h-[660px]">
              <RouteComponent />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrekkingInfo;
